<template>
  <v-card width="100%" elevation="0">
    <v-card-title class="pb-0 pl-1">Tier Levels</v-card-title>

    <div class="pl-1">
      <div>
        <div class="d-flex overline" style="align-items: center; gap: 8px;">
          <b class="blue--text">Low tier:</b>
          <v-btn
            elevation="0"
            x-small
            color="primary"
            @click="addTier('lowTiers')"
          >
            Add
          </v-btn>
        </div>

        <div class="d-flex" style="flex-direction: column; gap: 2px;">
          <div
            v-for="(tier, index) in lowTiers"
            :key="`low-${index}`"
            class="tl__rewards tl__rewards--low d-flex justify-space-between"
            style="gap: 8px; align-items: center"
          >
            <v-expansion-panels style="width: 100%">
              <r-reward-track-panel :model.sync="tier.rewards" title="Reward" />
            </v-expansion-panels>

            <v-text-field
              v-model.number="tier.minLevel"
              label="Min Level"
              hide-details
              type="number"
              class="pt-0 mt-0"
            />

            <v-icon
              class="mr-2"
              small
              color="teal lighten-1"
              @click.stop="copyTier('lowTiers', index)"
            >
              mdi-content-copy
            </v-icon>
            <v-icon
              class="mr-2"
              small
              color="red"
              @click.stop="removeTier('lowTiers', index)"
            >
              mdi-delete
            </v-icon>
          </div>
        </div>
      </div>

      <div class="my-2">
        <div class="d-flex overline" style="align-items: center; gap: 8px;">
          <b class="blue--text">Middle tier:</b>
          <v-btn
            elevation="0"
            x-small
            color="primary"
            @click="addTier('midTiers')"
          >
            Add
          </v-btn>
        </div>

        <div class="d-flex" style="flex-direction: column; gap: 2px;">
          <div
            v-for="(tier, index) in midTiers"
            :key="`mid-${index}`"
            class="tl__rewards tl__rewards--mid d-flex justify-space-between"
            style="gap: 8px; align-items: center"
          >
            <v-expansion-panels style="width: 100%">
              <r-reward-track-panel :model.sync="tier.rewards" title="Reward" />
            </v-expansion-panels>

            <v-text-field
              v-model.number="tier.minLevel"
              label="Min Level"
              hide-details
              type="number"
              class="pt-0 mt-0"
            />

            <v-icon
              class="mr-2"
              small
              color="teal lighten-1"
              @click.stop="copyTier('midTiers', index)"
            >
              mdi-content-copy
            </v-icon>
            <v-icon
              class="mr-2"
              small
              color="red"
              @click.stop="removeTier('midTiers', index)"
            >
              mdi-delete
            </v-icon>
          </div>
        </div>
      </div>

      <div>
        <div class="d-flex overline" style="align-items: center; gap: 8px;">
          <b class="blue--text">High tier:</b>
          <v-btn
            elevation="0"
            x-small
            color="primary"
            @click="addTier('highTiers')"
          >
            Add
          </v-btn>
        </div>

        <div class="d-flex" style="flex-direction: column; gap: 2px;">
          <div
            v-for="(tier, index) in highTiers"
            :key="`high-${index}`"
            class="tl__rewards tl__rewards--high d-flex justify-space-between"
            style="gap: 8px; align-items: center"
          >
            <v-expansion-panels style="width: 100%">
              <r-reward-track-panel :model.sync="tier.rewards" title="Reward" />
            </v-expansion-panels>

            <v-text-field
              v-model.number="tier.minLevel"
              label="Min Level"
              hide-details
              type="number"
              class="pt-0 mt-0"
            />

            <v-icon
              class="mr-2"
              small
              color="teal lighten-1"
              @click.stop="copyTier('highTiers', index)"
            >
              mdi-content-copy
            </v-icon>
            <v-icon
              class="mr-2"
              small
              color="red"
              @click.stop="removeTier('highTiers', index)"
            >
              mdi-delete
            </v-icon>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import VueTypes from "vue-types";
import {serialize, deserialize} from "serializr";
import {ChristmasTierLevels, ChristmasTierEntry} from "@/modules/redactor/models/smart-offer/bundle-christmas/ChristmasTierLevels";
import RRewardTrackPanel from "@/modules/redactor/views/reward/r-reward-track-panel.vue";

export default {
  name: "r-christmas-tiers",
  components: { RRewardTrackPanel },

  props: {
    model: VueTypes.instanceOf(ChristmasTierLevels).isRequired,
  },

  computed: {
    lowTiers: {
      get() {
        return this.model.tier_low || [];
      },
      set(val) {
        this.model.tier_low = val;
      },
    },
    midTiers: {
      get() {
        return this.model.tier_mid || [];
      },
      set(val) {
        this.model.tier_mid = val;
      },
    },
    highTiers: {
      get() {
        return this.model.tier_high || [];
      },
      set(val) {
        this.model.tier_high = val;
      },
    },
  },

  created() {
    if (!this.lowTiers.length) {
      this.addTier('lowTiers');
    }
    if (!this.midTiers.length) {
      this.addTier('midTiers');
    }
    if (!this.highTiers.length) {
      this.addTier('highTiers');
    }
  },

  methods: {
    addTier(kind) {
      const tiers = this[kind].slice();
      tiers.push(new ChristmasTierEntry(null, null));
      this[kind] = tiers;
    },

    removeTier(kind, index) {
      this.$confirm("Are you sure you want to delete this item?").then(() => {
        const tiers = this[kind].slice();
        tiers.splice(index, 1);
        this[kind] = tiers;
      });
    },

    copyTier(kind, index) {
      const tiers = this[kind].slice();
      const src = tiers[index];
      if (!src) return;

      const cloned = deserialize(
        ChristmasTierEntry,
        serialize(ChristmasTierEntry, src)
      );

      tiers.splice(index + 1, 0, cloned);

      this[kind] = tiers;
    },
  },
};
</script>
